import React, { useState, useEffect } from "react";

import { useAuth } from "../contexts/AuthContext";
import { AnimatedThemeToggler } from "./ui/animated-theme-toggler";
import apiService from "../services/api";
import { useAnalytics } from "../hooks/useAnalytics";
import { X, MessageSquare } from "lucide-react";


const Header = ({ user, onSidebarToggle, sidebarOpen }) => {
  const { logout } = useAuth();
  const analytics = useAnalytics();
  const [showUserMenu,           setShowUserMenu]           = useState(false);
  const [showNotifications,      setShowNotifications]      = useState(false);
  const [isNotificationsClosing, setIsNotificationsClosing] = useState(false);
  const [isUserMenuClosing,      setIsUserMenuClosing]      = useState(false);
  const [notifications,          setNotifications]          = useState([]);
  const [unreadCount,            setUnreadCount]            = useState(0);
  const [loading,                setLoading]                = useState(false);

  /* ── All original logic unchanged ── */
  const handleLogout = () => {
    analytics.trackLogout();
    analytics.trackEvent("logout_clicked", { category: "authentication", location: "header_menu" });
    setShowUserMenu(false);
    logout();
  };

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiService.getNotifications(1, 10, true);
      setNotifications(response.requests || []);
      setUnreadCount(response.pending_count || 0);
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (requestId) => {
    try {
      await apiService.markNotificationAsRead(requestId);
      setNotifications((prev) => prev.filter((n) => n.id !== requestId));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (_) {}
  };

  const markAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications([]);
      setUnreadCount(0);
    } catch (_) {}
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "";
    let date;
    if (dateString.includes("T")) {
      date = new Date(dateString);
    } else {
      date = new Date(dateString.replace(" ", "T") + "Z");
    }
    if (isNaN(date.getTime())) return "";
    const now = new Date();
    const diffInMs = now - date;
    if (diffInMs < 0) return "Agora";
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours   = Math.floor(diffInMinutes / 60);
    const diffInDays    = Math.floor(diffInHours / 24);
    if (diffInMinutes < 1)  return "Agora";
    if (diffInMinutes < 60) return `${diffInMinutes} min atrás`;
    if (diffInHours < 24)   return `${diffInHours}h atrás`;
    if (diffInDays < 7)     return `${diffInDays}d atrás`;
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  };

  useEffect(() => {
    if (!user) { setNotifications([]); setUnreadCount(0); return; }
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, [user]);

  const handleNotificationToggle = () => {
    if (showNotifications) {
      setIsNotificationsClosing(true);
      setTimeout(() => { setShowNotifications(false); setIsNotificationsClosing(false); }, 200);
    } else {
      setShowNotifications(true);
      loadNotifications();
    }
  };

  const handleUserMenuToggle = () => {
    if (showUserMenu) {
      setIsUserMenuClosing(true);
      setTimeout(() => { setShowUserMenu(false); setIsUserMenuClosing(false); }, 200);
    } else {
      setShowUserMenu(true);
    }
  };

  /* ── New visual ── */
  return (
    <header className="w-full h-16 flex-shrink-0 bg-surface/80 backdrop-blur-xl flex items-center justify-between px-6 border-b border-white/5 font-headline text-sm font-medium z-30 relative">

      {/* Left — search + mobile toggle */}
      <div className="flex items-center gap-4">
        {/* Mobile hamburger */}
        <button
          onClick={onSidebarToggle}
          className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-surface-container-high transition-colors"
        >
          <span className="material-symbols-outlined">menu</span>
        </button>

        {/* Search */}
        <div className="relative hidden md:block w-80">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-lg">search</span>
          <input
            className="w-full bg-surface-container-highest border-none rounded-lg pl-10 pr-4 py-2 text-sm text-on-surface focus:ring-1 focus:ring-primary focus:outline-none placeholder-zinc-500 transition-all"
            placeholder="Buscar interações, contatos..."
            type="text"
            readOnly
          />
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-2">

        {/* Theme toggle — AnimatedThemeToggler (Magic UI) */}
        <AnimatedThemeToggler />

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={handleNotificationToggle}
            className="w-10 h-10 flex items-center justify-center rounded-lg text-zinc-400 hover:bg-surface-container-high transition-colors relative"
          >
            <span className="material-symbols-outlined">notifications</span>
            {unreadCount > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-primary rounded-full border-2 border-surface" />
            )}
          </button>

          {showNotifications && (
            <div
              className={`absolute right-0 mt-2 w-[min(320px,calc(100vw-1rem))] bg-surface-container rounded-xl shadow-2xl border border-outline-variant/10 z-[100] overflow-hidden transition-all duration-200
                ${isNotificationsClosing ? "animate-slide-out-to-top opacity-0 scale-95" : "animate-slide-in-from-top opacity-100 scale-100"}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary text-lg">notifications</span>
                  <span className="text-sm font-bold text-on-surface">Notificações</span>
                  {unreadCount > 0 && (
                    <span className="bg-primary text-on-primary text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                  )}
                </div>
                {notifications.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const items = document.querySelectorAll("[data-notif-item]");
                      items.forEach((el, i) => {
                        setTimeout(() => {
                          el.style.transform = "translateX(100%)";
                          el.style.opacity   = "0";
                          el.style.transition = "all 0.4s ease";
                        }, i * 80);
                      });
                      setTimeout(() => {
                        markAllAsRead();
                        setIsNotificationsClosing(true);
                        setTimeout(() => { setShowNotifications(false); setIsNotificationsClosing(false); }, 200);
                      }, items.length * 80 + 400);
                    }}
                    className="text-[11px] text-zinc-500 hover:text-primary transition-colors"
                  >
                    Limpar tudo
                  </button>
                )}
              </div>

              {/* List */}
              <div className="max-h-96 overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-xs text-zinc-500">Carregando...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <span className="material-symbols-outlined text-4xl text-zinc-600 mb-2 block">notifications_off</span>
                    <p className="text-sm text-zinc-400 font-medium">Tudo em dia! 🎉</p>
                    <p className="text-xs text-zinc-600 mt-1">Nenhuma notificação pendente</p>
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {notifications.map((req, i) => {
                      const phoneNumber  = req.chat_id?.split("@")[0];
                      const isInstagram  = req.chat_id && !req.chat_id.includes("@s.whatsapp.net");
                      const formattedPhone = phoneNumber?.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, "+$1 ($2) $3-$4");
                      return (
                        <div
                          key={req.id}
                          data-notif-item
                          className="animate-slide-in-from-right"
                          style={{ animationDelay: `${i * 80}ms` }}
                        >
                          <div
                            onClick={() => {
                              markAsRead(req.id);
                              if (!isInstagram) {
                                const phone = req.chat_id.replace(/\D/g, "").replace("swhatsappnet", "");
                                window.location.href = `/chats?phone=${phone}&notificationId=${req.id}`;
                              }
                            }}
                            className={`p-3 rounded-lg cursor-pointer transition-all hover:scale-[1.02]
                              ${isInstagram
                                ? "bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/20"
                                : "bg-primary/10 border border-primary/20 hover:bg-primary/15"
                              }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-base text-primary">
                                  {isInstagram ? "photo_camera" : "support_agent"}
                                </span>
                                <span className="text-xs font-bold text-on-surface">
                                  {isInstagram ? "Instagram" : "🚨 Ajuda Humana"}
                                </span>
                              </div>
                              <button
                                onClick={(e) => { e.stopPropagation(); markAsRead(req.id); }}
                                className="p-0.5 rounded hover:bg-white/10 transition-colors"
                              >
                                <X className="w-3 h-3 text-zinc-500" />
                              </button>
                            </div>
                            <p className="text-[11px] text-zinc-400 truncate">{formattedPhone || phoneNumber}</p>
                            {req.last_message_snippet && (
                              <p className="text-[11px] text-zinc-500 mt-1 line-clamp-1">"{req.last_message_snippet}"</p>
                            )}
                            <div className="flex items-center justify-between mt-1.5">
                              <span className="text-[10px] text-zinc-600">{formatTimeAgo(req.timestamp)}</span>
                              {!isInstagram && (
                                <div className="flex items-center gap-1 text-[10px] text-primary font-bold">
                                  <MessageSquare className="w-3 h-3" />
                                  Abrir Chat
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-white/10 mx-1" />

        {/* User menu */}
        <div className="relative">
          <button
            onClick={handleUserMenuToggle}
            className="flex items-center gap-3 pl-2 pr-1 py-1 rounded-full hover:bg-surface-container-high transition-colors group"
          >
            <div className="text-right hidden sm:block">
              <p className="text-xs font-bold text-on-surface leading-none">{user?.name}</p>
              <p className="text-[10px] text-zinc-500 leading-none mt-0.5">
                {user?.role === "admin" ? "Administrador" : "Plano Enterprise"}
              </p>
            </div>
            <div className="w-8 h-8 rounded-full bg-surface-container-highest border border-white/10 flex items-center justify-center overflow-hidden">
              <span className="material-symbols-outlined text-zinc-400 text-xl">account_circle</span>
            </div>
            <span className="material-symbols-outlined text-zinc-500 text-lg group-hover:text-zinc-300">expand_more</span>
          </button>

          {showUserMenu && (
            <div
              className={`absolute right-0 mt-2 w-48 bg-surface-container rounded-xl shadow-2xl border border-outline-variant/10 z-50 overflow-hidden transition-all duration-200
                ${isUserMenuClosing ? "animate-slide-out-to-top opacity-0 scale-95" : "animate-slide-in-from-top opacity-100 scale-100"}`}
            >
              <div className="px-4 py-3 border-b border-white/5">
                <p className="text-sm font-bold text-on-surface truncate">{user?.name}</p>
              </div>
              <div className="py-1">
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full gap-3 px-4 py-3 text-sm text-error hover:bg-error/10 transition-colors"
                >
                  <span className="material-symbols-outlined text-base">logout</span>
                  Sair
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

    </header>
  );
};

export default Header;
